import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { 
  Divider, 
  Paper, 
  TextField, 
  Button, 
  MenuItem,
  Select,
  FormControl,
  Typography,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { 
  AccessTime as EstimatedTimeIcon,
  PersonOutlineOutlined as PersonIcon,
  KeyboardOutlined as UpdatedByIcon,
  LabelOutlined as LabelIcon,
  BookmarkBorderOutlined as CategoryIcon,
  Error as PriorityHighIcon,
  InboxOutlined as StatusIcon,
  BorderColor,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
// import styles from './TesCaseAddAndEditView.module.scss';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MultiLabelInput from '../MultiLabelInput/MultiLabelInput';
import useInputValidation from '../../utils/useInputValidation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// Import WYSIWYG editor components
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import { TestCaseTypes, PriorityTypes, StatusTypes, DEFAULT_STATUS, DEFAULT_PRIORITY, DEFAULT_TYPE, AutomationStatusEnum, DEFAULT_AUTOMATION_STATUS, ExecutionModeEnum, DEFAULT_EXECUTION_MODE } from "../../enums/enumRegistry";
import { checkExecutionPlanExists } from "../../utils/api";
// Import new S3 component
import FileSelectionWithPreview from '../TestCase/FileSelectionWithPreview';
import RichTextField from '../inputfield/RichTextField';
import { PlusIcon, XMarkIcon, DocumentTextIcon, TagIcon, CalendarIcon, CheckIcon, SparklesIcon, PaperClipIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import AttachmentViewerS3 from '../Attachment/AttachmentViewerS3';
import { useSnackbar } from "../../utils/useSnackbar";
import { breakpoints } from '../../theme/theme';
import styles from '../../styles/testcase/testcsaedetailsview.module.scss';
import ButtonComponent from '../customButton';
import { InputBox, Selectbox } from '../FormComponent';
import { convertPlaintextToRawFormat, convertToEditorFormat, isDraftRawFormat } from '../../utils/richTextEditorHelperFunction';
// Remove old arrays, use enums

const TestCaseAddAndEditView = ({ 
  open, 
  onClose, 
  onConfirm, 
  testCaseData, 
  modulePathForBreadcrumbs, // <-- add this prop
  onLabelCreated, // Callback when a label is created (for schema refresh)
}) => {

  const theme = useTheme();

  const [formData, setFormData] = useState({
    title: '',
    description: EditorState.createEmpty(),
    testdata: '',
    labels: [], 
    type: DEFAULT_TYPE,
    status: DEFAULT_STATUS,
    priority: DEFAULT_PRIORITY,
    attachments: [],
    estimated_time: 15,
    automation_status: DEFAULT_AUTOMATION_STATUS,
    execution_mode: DEFAULT_EXECUTION_MODE,
  });

  const { showSnackbar } = useSnackbar();
  // Add selectedFiles state for S3 migration
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  // Add state for tracking removed existing attachments
  const [removedAttachments, setRemovedAttachments] = useState([]);
  // Add loading state for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Add state for execution plan validation
  const [executionPlanExists, setExecutionPlanExists] = useState(false);
  const [checkingExecutionPlan, setCheckingExecutionPlan] = useState(false);

  // Effect to manage uploading state based on selectedFiles
  useEffect(() => {
    const hasUploadingFiles = selectedFiles.some(file => file.uploading === true);
    setIsUploadingFiles(hasUploadingFiles);
  }, [selectedFiles]);

  // Max-length validation hook for title (length > 200 shows inline error while typing)
  const { error: titleError, helperText: titleHelperText, validateInput: validateTitle } = useInputValidation(200);
  // Submit-time required/whitespace error for title (cleared as soon as user types)
  const [titleRequiredError, setTitleRequiredError] = useState('');

  const isEditing = !!testCaseData;

  // Helper function to create EditorState
  const createEditorState = (content) => {
    if (!content) return EditorState.createEmpty();
    try {
      if(isDraftRawFormat(content)) {
        return EditorState.createWithContent(convertToEditorFormat(content));
      }
      const contentBlocks = convertFromHTML(content);
      const contentState = ContentState.createFromBlockArray(
        contentBlocks.contentBlocks,
        contentBlocks.entityMap
      );
      return EditorState.createWithContent(contentState);
    } catch (error) {
      console.error('Error creating EditorState:', error);
      return EditorState.createEmpty();
    }
  };

  useEffect(() => {
    // Reset local states on open
    setSelectedFiles([]);
    setRemovedAttachments([]);
    setIsSubmitting(false);
    setIsUploadingFiles(false);
    setExecutionPlanExists(false);
    setTitleRequiredError('');

    // Initialize attachments for rendering
    if (isEditing && testCaseData?.attachments) {
      setFormData(prev => ({ ...prev, attachments: testCaseData.attachments }));
    }

    // Check execution plan if editing and test case has NLP automation status
    if (isEditing && testCaseData?.id && testCaseData?.automation_status === AutomationStatusEnum.NLP_AUTOMATED) {
      checkExecutionPlan(testCaseData.id);
    }

  }, [open]);

  // Reset form when modal opens or testCaseData changes
  useEffect(() => {
    if (open) {
      if (testCaseData) {
        setFormData({
          title: testCaseData.title || '',
          description: createEditorState(testCaseData.description),
          testdata: testCaseData.testdata ? JSON.stringify(testCaseData.testdata) : '',
          labels: testCaseData.labels || [], // Update to handle labels array
          type: testCaseData.type || DEFAULT_TYPE,
          status: testCaseData.status || DEFAULT_STATUS,
          priority: testCaseData.priority || DEFAULT_PRIORITY,
          attachments: testCaseData.attachments || [],
          estimated_time: testCaseData.estimated_duration !== undefined ? testCaseData.estimated_duration : 15,
          automation_status: Object.values(AutomationStatusEnum).includes(testCaseData.automation_status)
            ? testCaseData.automation_status
            : DEFAULT_AUTOMATION_STATUS,
          execution_mode: Object.values(ExecutionModeEnum).includes(testCaseData.execution_mode)
            ? testCaseData.execution_mode
            : DEFAULT_EXECUTION_MODE,
        });
        resetInputValidation();
      } else {
        // Reset for new test case
        setFormData({
          title: '',
          description: EditorState.createEmpty(),
          testdata: '',
          labels: [], // Initialize as empty array
          type: DEFAULT_TYPE,
          status:  DEFAULT_STATUS,
          priority:  DEFAULT_PRIORITY,
          attachments: [],
          estimated_time: 15,
          automation_status: DEFAULT_AUTOMATION_STATUS,
          execution_mode: DEFAULT_EXECUTION_MODE,
        });
      }

    }
  }, [open, testCaseData]);

  //Function to reset input validation
  const resetInputValidation = () => {
    validateTitle(testCaseData?.title || '');
  };

  // Handle description change
  const handleDescriptionChange = (editorState) => {
    setFormData(prev => ({
      ...prev,
      description: editorState
    }));
  };

  // Handle files selected from FileSelectionWithPreview component
  const handleFilesSelected = (files) => {
    setSelectedFiles(files);
    
    // Check if any files are currently uploading
    const hasUploadingFiles = files.some(file => file.uploading === true);
    setIsUploadingFiles(hasUploadingFiles);
  };

  // Get temp file keys for form submission
  const getTempFileKeys = () => {
    return selectedFiles
      .filter(file => file.uploaded && file.fileKey)
      .map(file => file.fileKey);
  };

  // Handle removal of existing attachments (optimistic UI)
  const handleRemoveExistingAttachment = (attachmentId) => {
    setRemovedAttachments(prev => [...prev, attachmentId]);
    setFormData(prev => ({
      ...prev,
      attachments: (prev.attachments || []).filter(att => att.id !== attachmentId),
    }));
  };

  // Handle removal of new files (this should be handled by FileSelectionWithPreview, but just in case)
  const handleRemoveNewFile = (fileId) => {
    setSelectedFiles(prev => {
      const updatedFiles = prev.filter(f => f.id !== fileId);
      // The useEffect will automatically update isUploadingFiles based on the new array
      return updatedFiles;
    });
  };

  // Parent no longer renders/removes new files; FileSelectionWithPreview handles this
  
  // Check if values have changed
  const isChanged = useMemo(() => {
    // Safely get HTML content from EditorState
    const getDescriptionHTML = (editorState) => {
      try {
        return draftToHtml(convertToRaw(editorState.getCurrentContent()));
      } catch (error) {
        return '';
      }
    };
    
    if (!testCaseData) {
      return (
        formData.title !== '' || 
        getDescriptionHTML(formData.description) !== '' ||
        formData.labels.length > 0 ||
        formData.type !== '' ||
        formData.status !== '' ||
        formData.priority !== '' ||
        formData.testdata !== '' ||
        formData.automation_status !== DEFAULT_AUTOMATION_STATUS ||
        formData.execution_mode !== DEFAULT_EXECUTION_MODE ||
        selectedFiles.length > 0
      );
    }
    
    return (
      formData.title !== (testCaseData.title || '') || 
      getDescriptionHTML(formData.description) !== (testCaseData.description || '') ||
      JSON.stringify(formData.labels) !== JSON.stringify(testCaseData.labels || []) ||
      formData.type !== (testCaseData.type || '') ||
      formData.status !== (testCaseData.status || '') ||
      formData.priority !== (testCaseData.priority || '') ||
      formData.testdata !== JSON.stringify(testCaseData.testdata || '') ||
      formData.automation_status !== (testCaseData.automation_status || DEFAULT_AUTOMATION_STATUS) ||
      formData.execution_mode !== (testCaseData.execution_mode || DEFAULT_EXECUTION_MODE) ||
      selectedFiles.length > 0 ||
      removedAttachments.length > 0
    );
  }, [formData, testCaseData, selectedFiles, removedAttachments]);

  // Function to check execution plan existence
  const checkExecutionPlan = async (testCaseId) => {
    if (!testCaseId) return false;
    setCheckingExecutionPlan(true);
    try {
      const exists = await checkExecutionPlanExists({ testCaseId });
      setExecutionPlanExists(exists);
      return exists;
    } catch (error) {
      console.error('Error checking execution plan:', error);
      setExecutionPlanExists(false);
      return false;
    } finally {
      setCheckingExecutionPlan(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));

    if (field === 'title') {
      validateTitle(e.target.value);
      if (titleRequiredError) setTitleRequiredError('');
    }
  };

  // Handle confirm button click
  const handleConfirm = async () => {
    // Guard: title is required — catch empty/whitespace-only before async work
    if (!formData.title.trim()) {
      setTitleRequiredError('Test Case Summary is required');
      return;
    }

    setIsSubmitting(true);
    // Validate NLP Automated status
    if (formData.automation_status === AutomationStatusEnum.NLP_AUTOMATED) {
      if (isEditing && testCaseData?.id) {
        // For editing, check if execution plan exists
        const hasExecutionPlan = await checkExecutionPlan(testCaseData.id);
        if (!hasExecutionPlan) {
          // Show error message
          showSnackbar('Cannot set automation status to "NLP Automated" without a valid execution plan. Please create an execution plan first.', 'error');
          return;
        }
      } else {
        // For new test cases, cannot set to NLP Automated
        showSnackbar('You can set NLP automation only after creating the test case and generating an execution plan. Please save the test case first, then create an execution plan in the Automation tab.', 'error');
        return;
      }
    }

    // Convert EditorState to HTML
    let descriptionHTML = '';
    try {
      descriptionHTML = draftToHtml(convertToRaw(formData.description.getCurrentContent()));
    } catch (error) {}

    // Prepare the test case object
    const testCaseToSave = testCaseData 
      ? { 
          ...testCaseData, 
          ...formData,
          description: descriptionHTML,
          labels: formData.labels,
          estimated_duration: formData.estimated_time,
          // Attachments meta for edit flow
          attachmentsMeta: { removedIds: removedAttachments },
        }
      : { 
          ...formData, 
          description: descriptionHTML,
          estimated_duration: formData.estimated_time,
        };
    
    try {
      // Convert EditorState to HTML
      let descriptionHTML = '';
      try {
        // descriptionHTML = draftToHtml(convertToRaw(formData.description.getCurrentContent()));
        descriptionHTML = convertPlaintextToRawFormat(formData.description.getCurrentContent())
      } catch (error) {}

      // Prepare the test case object
      const testCaseToSave = testCaseData 
        ? { 
            ...testCaseData, 
            ...formData,
            description: descriptionHTML,
            labels: formData.labels,
            estimated_duration: formData.estimated_time,
            // Attachments meta for edit flow
            attachmentsMeta: { removedIds: removedAttachments },
            tempFileKeys: getTempFileKeys(),
          }
        : { 
            ...formData, 
            description: descriptionHTML,
            estimated_duration: formData.estimated_time,
            tempFileKeys: getTempFileKeys(),
          };
      
      await onConfirm(testCaseToSave, selectedFiles);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    try {
      return (
        formData.title
      );
    } catch (error) {
      return false;
    }
  };

  const isInputValid = () => {
    return (
      !titleError
    );
  };

  // Check if form is valid for NLP Automated status
  const isNLPStatusValid = () => {
    if (formData.automation_status === AutomationStatusEnum.NLP_AUTOMATED) {
      if (isEditing) {
        return executionPlanExists;
      } else {
        return false; // Cannot set NLP Automated for new test cases
      }
    }
    return true;
  };


  // Handle drawer close with reason checking 
  const handleDrawerClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleDrawerClose}
      anchor="right"
      BackdropProps={{
        onClick: (e) => e.stopPropagation(),
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.25)',
        },
      }}
      className={styles.silderContainer}
    >
      <Paper className={styles.innerContainer}>
        {/* Header */}
        <Box className={styles.headerContainer}>
          <Box className="icon-cover bg-green600">
            <PlusIcon className="icon white" />
          </Box>
          <Box className={styles.headerTitleContainer}>
            <Typography variant="componentTitle">
              {isEditing ? 'Edit Test Case' : 'Create New Test Case'}
            </Typography>
              <Box className={styles.subTitleContainer}>
                {
                  (()=>{
                    const pathArr = isEditing && testCaseData?.module?.modulePath && testCaseData.module.modulePath.length > 0
                    ? testCaseData.module.modulePath
                    : Array.isArray(modulePathForBreadcrumbs) ? modulePathForBreadcrumbs : [];
                    return pathArr.length > 0 ? (
                      pathArr.map((m, idx) => (
                        <React.Fragment key={m.id || m.name || idx}>
                          <Typography variant="componentTitleSmall">
                            {m.name}
                          </Typography>
                          {idx < pathArr.length - 1 && (
                            <span className={styles.dot}>•</span>
                          )}
                        </React.Fragment>
                      ))
                    ) : null;
                  })()
                }
              </Box>
          </Box>
          <IconButton
            className={styles.closeIcon}
            onClick={onClose}
            title="Close"
          >
            <XMarkIcon className="icon" />
          </IconButton>
        </Box>

        <Box className={styles.secondaryHeaderContainertwo}>
          <Box className="icon-cover bg-green200">
            <DocumentTextIcon className="icon green400" />
          </Box>
          <Box className={styles.subTitleContainer}>
              <p className={styles.title}>Test Case Information</p>
              <p className={styles.subtitle}>{isEditing ? 'Update the details to edit the test case.' : 'Fill in the details to create a new test case.'}</p>
          </Box>
        </Box>

        {/* Content Section - Scrollable */}
        <Box className={`${styles.contentContainer} ${styles.contentSpacing}`}>

          {/* Section 1: Test Case Overview */}
          <Box className={`${styles.sectionContainer}`}>
            <Box className={styles.containerOne}>
              <Box className="icon-cover-medium bg-blue100">
                <DocumentTextIcon className="icon-medium blue600" />
              </Box>
              <p>Test Case Overview</p>
            </Box>
            
            {/* Test Case Summary */}
            <Box>
              <p className={styles.title}>Test Case Summary *</p>
              <InputBox
                value={formData.title}
                onChange={handleInputChange('title')}
                fullWidth
                variant="outlined"
                placeholder="Enter a clear and concise summary of what this test case validates..."
                multiline
                size="small"
                error={titleRequiredError || titleHelperText || false}
                helperText={
                  titleRequiredError || titleHelperText
                    ? titleRequiredError || titleHelperText
                    : `${formData.title.trim().length}/200 characters`
                }
                minRows={3}
                maxRows={5}
                className={styles.textField}
                endIcon={<XMarkIcon className="icon" />}
                onEndIconClick={() => {
                  setFormData(prev => ({ ...prev, title: '' }));
                  validateTitle('');
                  setTitleRequiredError('');
                }}
              />
            </Box>
            {/* Test Case Description */}
            <Box>
              <p className={styles.title}>Test Case Description</p>
              <Box className={styles.richTextFieldContainer}>
                <RichTextField
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  error={false}
                  helperText=""
                  placeholder="Provide a detailed description of what this test case validates, including the scope, objectives, and any important context..."
                />
              </Box>
            </Box>
          </Box>

          {/* Section 2: Test Classification */}
          <Box className={`${styles.sectionContainer}`}>
            <Box className={styles.containerOne}>
              <Box className="icon-cover-medium bg-blue100">
                <TagIcon className="icon-medium blue600" />
              </Box>
              <p>Test Classification</p>
            </Box>
            <Box className={styles.containerTwo}>
              <Box className={styles.flexRowTwo}>
                <Box>
                  <p className={styles.title}>Test Type *</p>
                  <Selectbox 
                    size="small"
                    value={formData.type}
                    onChange={handleInputChange('type')}
                    options={Object.values(TestCaseTypes).map((type) => ({
                      label: type,
                      value: type
                    }))}
                    required={true}
                    placeholder="Choose a test type"
                    endIcon={formData.type !== DEFAULT_TYPE && <XMarkIcon className="icon" />}
                    onEndIconClick={() => setFormData(prev => ({ ...prev, type: DEFAULT_TYPE }))}
                  />
                </Box>
                <Box>
                  <p className={styles.title}>Test Case Status *</p>
                  <Selectbox 
                    size="small"
                    value={formData.status}
                    onChange={handleInputChange('status')}
                    options={Object.values(StatusTypes).map((status) => ({
                      label: status,
                      value: status
                    }))}
                    required={true}
                    placeholder="Choose a test status"
                    endIcon={formData.status !== DEFAULT_STATUS && <XMarkIcon className="icon" />}
                    onEndIconClick={() => setFormData(prev => ({ ...prev, status: DEFAULT_STATUS }))}
                  />
                </Box>
              </Box>
              <Box className={styles.flexRowTwo}>
                <Box>
                  <p className={styles.title}>Priority *</p>
                  <Selectbox 
                    size="small"
                    value={formData.priority}
                    onChange={handleInputChange('priority')}
                    options={Object.values(PriorityTypes).map((priority) => ({
                      label: priority,
                      value: priority
                    }))}
                    required={true}
                    placeholder="Choose a test priority"
                    endIcon={formData.priority !== DEFAULT_PRIORITY && <XMarkIcon className="icon" />}
                    onEndIconClick={() => setFormData(prev => ({ ...prev, priority: DEFAULT_PRIORITY }))}
                  />
                </Box>
                <Box>
                  <p className={styles.title}>Estimated Time (minutes)</p>
                  <InputBox
                    type="number"
                    min={1}
                    value={formData.estimated_time === undefined ? 15 : formData.estimated_time}
                    onChange={e => {
                      const value = Number(e.target.value);
                      setFormData(prev => ({ ...prev, estimated_time: value < 1 ? 1 : value }));
                    }}
                    fullWidth
                    size="small"
                    className={styles.textField}
                  />
                </Box>
              </Box>
              <Box className={styles.flexRowTwo}>
                <Box>
                  <p className={styles.title}>Automation Status</p>
                  <Selectbox
                    size="small"
                    value={formData.automation_status}
                    onChange={async (e) => {
                      const value = e.target.value;
                      let execution_mode = formData.execution_mode;
                      if (value === AutomationStatusEnum.NOT_AUTOMATED) execution_mode = ExecutionModeEnum.MANUAL;
                      else if (value === AutomationStatusEnum.NLP_AUTOMATED) {
                        execution_mode = ExecutionModeEnum.NLP;
                        // Check execution plan if editing
                        if (isEditing && testCaseData?.id) {
                          await checkExecutionPlan(testCaseData.id);
                        }
                      }
                      else if (value === AutomationStatusEnum.EXTERNALLY_AUTOMATED) execution_mode = ExecutionModeEnum.EXTERNAL;
                      else if (value === AutomationStatusEnum.IN_PROGRESS) execution_mode = null;
                      setFormData(prev => ({
                        ...prev,
                        automation_status: value,
                        execution_mode,
                      }));
                    }}
                    options={Object.values(AutomationStatusEnum).map((automation_status) => ({
                      label: automation_status,
                      value: automation_status
                    }))}
                  />
                  {/* Warning message for NLP Automated without execution plan */}
                  {formData.automation_status === AutomationStatusEnum.NLP_AUTOMATED && 
                  (checkingExecutionPlan || 
                    (isEditing && !executionPlanExists) || 
                    !isEditing) && (
                    <Box className={styles.warningMessage}>
                      <Box className={styles.warningContent}>
                        <InformationCircleIcon className="icon-lg" />
                        <Typography variant="body2" className={styles.warningText}>
                          {checkingExecutionPlan ? 'Checking execution plan...' : 
                          isEditing && !executionPlanExists ? 'No execution plan found. Please create an execution plan in the Automation tab first.' :
                          !isEditing ? 'You can set NLP automation only after creating the test case and generating an execution plan.' :
                          ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box>
                  <p className={styles.title}>Execution Mode</p>
                  <Selectbox
                    size="small"
                    value={formData.execution_mode}
                    onChange={handleInputChange('execution_mode')}
                    disabled
                    options={Object.values(ExecutionModeEnum).map((execution_mode) => ({
                      label: execution_mode,
                      value: execution_mode
                    }))}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Section 3: Labels */}
          <Box className={`${styles.sectionContainer}`}>
            <Box className={styles.containerOne}>
              <Box className="icon-cover-medium bg-blue100">
                <TagIcon className="icon-medium blue600" />
              </Box>
              <p>Labels</p>
            </Box>
            <MultiLabelInput
              value={formData.labels}
              onChange={(newLabels) => {
                setFormData(prev => ({
                  ...prev,
                  labels: newLabels
                }));
              }}
              placeholder="Add a custom label..."
              onLabelCreated={onLabelCreated}
            />
          </Box>

          {/* Section 4: Attachments */}
          <Box className={`${styles.sectionContainer}`}>
            <Box className={styles.containerOne}>
              <Box className="icon-cover-medium bg-blue100">
                <PaperClipIcon className="icon-medium blue600" />
              </Box>
              <p>Attachments</p>
              <p>(Optional)</p>
            </Box>  

            {/* Show existing attachments in edit mode */}
            {isEditing && formData.attachments && formData.attachments.length > 0 && (
              <Box className={styles.existingAttachments}>
                <Typography variant="subtitle2" className={styles.existingAttachmentsTitle}>
                  Existing Attachments
                </Typography>
                <AttachmentViewerS3 
                  attachments={formData.attachments} 
                  canEdit={true}
                  onDelete={handleRemoveExistingAttachment}
                />
              </Box>
            )}
            
            {/* File upload for new attachments (same as create mode) */}
            <FileSelectionWithPreview
              onFilesSelected={handleFilesSelected}
            />
          </Box>

          {/* Tips Section - now inside scrollable */}
          <Box className={`${styles.sectionContainer} bg-blue50`}>
            <Box className={styles.containerOne}>
              <Box className="icon-cover-medium bg-blue100">
                <InformationCircleIcon className="icon-medium blue600" />
              </Box>
              <p className={styles.tipsTitle}>💡Tips for Writing Effective Test Cases</p>
            </Box>
            <Box className={styles.tipsContainer}>
              <Box>
                <p>•  Be specific and clear in your descriptions</p>
                <p>•  Include both positive and negative test scenarios</p>
                <p>•  Define clear, measurable expected results</p>
              </Box>
              <Box>
                <p>•  Consider edge cases and error conditions</p>
                <p>•  Make prerequisites realistic and achievable</p>
                <p>•  Use appropriate labels for better organization</p>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box className={`${styles.sectionContainer} ${styles.flexRow} ${styles.footerContainer}`}>
          <Box className={styles.containerOne}>
            <CalendarIcon className="icon-lg slate600" />
            <p>{isEditing ? 'Updating test case' : 'Creating new test case'}</p>
          </Box>
          <Box className={styles.actionButtonsContainer}>
            <ButtonComponent
              variant="outline"
              onClick={onClose}
              className={`${styles.cancelButton} slate600`}
              ctaText="Cancel"
            />
            <ButtonComponent
              variant="primary"
              onClick={handleConfirm}
              disabled={!isChanged || !isInputValid() || isSubmitting || !isNLPStatusValid()}
              className={`${styles.saveButton} white bg-green600`}
              ctaText={
                isUploadingFiles ? 'Uploading Files...' :
                isSubmitting ? (isEditing ? 'Saving Changes...' : 'Creating Test Case...') :
                (isEditing ? 'Save Changes' : 'Create Test Case')
              }
              startIcon={isSubmitting ? <CircularProgress className="icon white" /> : <CheckIcon className="icon white" />}
            />
          </Box>
        </Box>
      </Paper>
    </Drawer>
  );
};

export default TestCaseAddAndEditView;